/**
 * SD-2708: copying the send-email card's contents to the clipboard.
 *
 * The card no longer hands the email to a mail client. The user copies it and pastes into
 * whatever they already use, which removes the `mailto:` URL ceiling entirely and — more to the
 * point — lets formatting survive, which a mailto body never could.
 *
 * Formatting survives because the clipboard carries several representations of the same
 * selection at once. Writing `text/html` alongside `text/plain` means Outlook, Gmail and Word
 * take the HTML and keep bold, bullets, headings and tables, while a plain-text target still
 * gets something readable rather than markup.
 *
 * Both writes need a secure context and a user gesture, so these are only ever called straight
 * from a click handler. Where the richer API is missing the plain text is still written, on the
 * grounds that a correctly-pasted plain summary beats a copy button that does nothing.
 */

export interface RichTextFlavours {
  /** Rendered HTML, taken by clients that understand it. */
  html: string;
  /** Readable fallback for plain-text targets. Never markup. */
  text: string;
}

/**
 * Write both representations. Resolves false when the copy did not happen, so the caller can
 * withhold the "Copied" confirmation rather than claiming success it cannot verify.
 */
export async function copyRichText(
  flavours: RichTextFlavours
): Promise<boolean> {
  const { html, text } = flavours;
  if (!html && !text) return false;

  if (supportsRichClipboard()) {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch {
      // Permission refused, or a browser that advertises ClipboardItem but rejects this
      // combination. The plain-text path below is still worth trying.
    }
  }

  return copyPlainText(text);
}

/** Write plain text only — the To and Subject fields, and the fallback for the body. */
export async function copyPlainText(value: string): Promise<boolean> {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function supportsRichClipboard(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function'
  );
}
